"use client";

import { useCallback, useEffect, useState, useTransition } from "react";
import { usePathname, useRouter } from "next/navigation";

import { isUserLoggedIn } from "@/hooks/auth/useLogin";
import { buildPathWithLocale, isSupportedLanguage } from "@/utils/locale";
import { getLanguageCookie, setLanguageCookie } from "@/services/LanguagePreferenceService";
import patchUserInformationHandler from "@/interface/api/patchUserInformation";

import type { LanguageCode } from "@/types/languages";
import { useApiWithLayout } from "../api";

export const useLanguagePreference = () => {
  const pathname = usePathname();
  const router = useRouter();

  const [isPending, startTransition] = useTransition();
  const [activeLocale, setActiveLocale] = useState<LanguageCode | null>(null);

  const isUserLogged = Boolean(isUserLoggedIn());

  const { request: requestUpdateUserInformation } =
    useApiWithLayout(patchUserInformationHandler.patchUserInformation) || {};

  useEffect(() => {
    const pathLocale = pathname.split("/")[1];

    if (isSupportedLanguage(pathLocale)) {
      setActiveLocale(pathLocale as LanguageCode);
      return;
    }
    setActiveLocale(getLanguageCookie());
  }, [pathname]);

  const handleLanguageChange = useCallback(
    async (locale: LanguageCode) => {
      if (!isSupportedLanguage(locale)) return;

      setLanguageCookie(locale);

      if (isUserLogged) {
        try {
          await requestUpdateUserInformation({ locale });
        } catch (error) {
          console.error("Failed to sync language preference", error);
          throw error;
        }
      }
    },
    [isUserLogged, requestUpdateUserInformation]
  );

  const navigateToLocale = useCallback(
    (nextLocale: LanguageCode) => {
      if (!isSupportedLanguage(nextLocale)) return;

      const nextPath = buildPathWithLocale(nextLocale, pathname);

      startTransition(() => {
        router.push(nextPath);
        router.refresh();
      });
    },
    [pathname, router, startTransition]
  );

  return {
    locale: activeLocale,
    handleLanguageChange,
    navigateToLocale,
    isNavigating: isPending,
  };
};
